import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  StyledModalContent,
  TitleText,
} from "./QueryScopeModal";
import StyledText from "../../components/StyledComponents/StyledText";
import { CorporateTheme, Theme, font } from "../../utils/themes";
import { Header } from "../forms/CustomForms/ShareFormModal";
import styled from "styled-components";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import DocumentIcon from "../../assets/icons/document-text";
import StyledSwitch from "../../components/StyledComponents/StyledSwitch";
import { StyledCheck } from "../../components/StyledComponents/CheckBox/StyledCheck";
import StyledCheckBox from "../../components/StyledComponents/CheckBox/StyledCheckBox";
import CustomSelect from "../../components/form/CustomSelect";
import StyledTimePicker from "../../components/StyledComponents/StyledTimePicker";
import CalendarIcon from "../../assets/icons/CalendarIcon";
import CustomInput from "../../components/form/CustomInput";
import StyledStepper from "../../components/StyledComponents/StylesStepper";
import {
  useGetQueryByIdMutation,
  useGetScheduleQueryByIdMutation,
  useScheduledQueryMutation,
} from "../../api/searchQueryApiSlice";
import { useToast } from "../../components/toast/ToastContext";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { SkeletonCell } from "../../components/SkeletonCell";
import { useTheme } from "../../middleware/ThemeContext";
import SaveIcon from "../../assets/icons/SaveIcon";
import { StyledTag } from "../../components/StyledTag";
import { hexToRgba } from "../../utils/helper";
import StyledTextEditor from "../../components/StyledComponents/TextEditor/StyledTextEditor";
import CheckIcon from "../../components/StyledComponents/CheckBox/CheckIcon";
import bgImage from "../../assets/images/CreateFormBackground.png";
import TooltipIcon from "../../assets/icons/Tooltip";
import CloseIcon from "../../assets/icons/CloseIcon";
import VerifyIcon from "../../assets/icons/VerifyIcon";
import { useGetFromEmailQuery } from "../../api/amsApiSlice";

type ScheduleQueryModalProps = {
  title?: string;
  subTitle?: string;
  onClose: () => void;
  name?: string;
  id?: string;
  refetch?: () => void;
};

const ScheduleQueryModal: React.FC<ScheduleQueryModalProps> = ({
  title,
  subTitle,
  onClose,
  refetch,
  name,
  id,
}) => {
  const { theme } = useTheme();
  type EmailFieldKey = "to" | "cc" | "bcc";

  const [scheduleData, setScheduleData] = useState<{
    isEnabled: boolean;
    daysOfWeek: string[];
    frequency: string;
    intervalHours?: number;
    dayOfMonth: { value: string; label: string };
    timeOfDay: string;
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    intervalHours: 1,
    isEnabled: false,
    daysOfWeek: [] as string[],
    frequency: "",
    dayOfMonth: { value: "", label: "" },
    timeOfDay: "",
    startDate: undefined,
    endDate: undefined,
  });

  const [isCarbonCopy, setIsCarbonCopy] = useState(false);
  const [fromEmail, setFromEmail] = useState<string>("");
  const [outputFormat, setOutputFormat] = useState<string>("CSV");
  const [skipIfEmpty, setSkipIfEmpty] = useState<boolean>(false);
  const [emailDrafts, setEmailDrafts] = useState<Record<EmailFieldKey, string>>({
    to: "",
    cc: "",
    bcc: "",
  });
  const [emailTags, setEmailTags] = useState<Record<EmailFieldKey, string[]>>({
    to: [],
    cc: [],
    bcc: [],
  });
  const [emailErrors, setEmailErrors] = useState<Record<EmailFieldKey, string>>({
    to: "",
    cc: "",
    bcc: "",
  });
  const [subject, setSubject] = useState("");
  const [outputFileName, setOutputFileName] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const [scheduleQuery, { isLoading }] = useScheduledQueryMutation();
  const { data: fromEmailOptions } = useGetFromEmailQuery({});
  const fromEmailSelectOptions = Array.isArray(fromEmailOptions?.senders)
    ? fromEmailOptions?.senders
    : [];

  const mappedFromEmailOptions = fromEmailSelectOptions
    .map((item: any) => {
      const email = item?.email

      if (!email) return null;

      return {
        label: email,
        value: email,
      };
    })
    .filter(Boolean) as { label: string; value: string }[];

  useEffect(() => {
    if (!fromEmail && mappedFromEmailOptions.length === 1) {
      setFromEmail(mappedFromEmailOptions[0].value);
    }
  }, [fromEmail, mappedFromEmailOptions]);

  const [getQueryData, { isLoading: getQueryLoading }] =
    useGetScheduleQueryByIdMutation();
  const showToast = useToast();

  const handleDayToggle = (day: string) => {
    setScheduleData((prev) => {
      const isAlreadySelected = prev.daysOfWeek?.includes(day);
      const updatedweekDays = isAlreadySelected
        ? prev?.daysOfWeek?.filter((d) => d !== day)
        : [...(prev?.daysOfWeek ?? []), day];

      // If user selected at least one weekday, clear the weekday validation error
      if (updatedweekDays && updatedweekDays.length > 0) {
        setFieldErrors((errs) => {
          const copy = { ...errs };
          if (copy.daysOfWeek) delete copy.daysOfWeek;
          return copy;
        });
      }

      return { ...prev, daysOfWeek: updatedweekDays };
    });
  };

  const dayStringToIndexMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thurs: 4, Fri: 5, Sat: 6,
  };

  const dayIndexToStringMap: Record<number, string> = {
    0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thurs", 5: "Fri", 6: "Sat",
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const parseEmailParts = (rawValue: string) => rawValue
    .split(/[,\n;]/)
    .map((part) => part.trim())
    .filter(Boolean);

  const commitEmailValue = (field: EmailFieldKey, rawValue: string) => {
    const parts = parseEmailParts(rawValue);

    if (!parts.length) return;

    // Once the user has entered content for this field, clear the required-state error.
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

    const invalidPart = parts.find((part) => !emailPattern.test(part));
    if (invalidPart) {
      setEmailErrors((prev) => ({
        ...prev,
        [field]: "Enter a valid email address",
      }));
      return;
    }

    setEmailTags((prev) => {
      const nextTags = [...prev[field]];
      parts.forEach((part) => {
        if (!nextTags.includes(part)) {
          nextTags.push(part);
        }
      });
      return {
        ...prev,
        [field]: nextTags,
      };
    });

    setEmailDrafts((prev) => ({ ...prev, [field]: "" }));
    setEmailErrors((prev) => ({ ...prev, [field]: "" }));
    // Clear field-level required validation error when tag is added
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleEmailKeyDown = (field: EmailFieldKey, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitEmailValue(field, emailDrafts[field]);
    }
  };

  const handleEmailBlur = (field: EmailFieldKey) => {
    if (emailDrafts[field].trim()) {
      commitEmailValue(field, emailDrafts[field]);
    }
  };

  const removeEmailTag = (field: EmailFieldKey, value: string) => {
    setEmailTags((prev) => ({
      ...prev,
      [field]: prev[field].filter((tag) => tag !== value),
    }));
  };

  const getOutputFormatLabel = (value: string) => {
    if (value === "EXCEL") return "Excel";
    if (value === "JSON") return "Json";
    if (value === "CSV") return "Csv";
    return value;
  };

  const getFileExtension = (format: string): string => {
    const extensionMap: Record<string, string> = {
      EXCEL: "xlsx",
      JSON: "json",
      CSV: "csv",
    };

    return extensionMap[format] || format.toLowerCase();
  };

  const formatOutputFileName = (fileName: string, format: string): string => {
    const trimmed = fileName.trim();
    if (!trimmed) return "";

    const extension = getFileExtension(format);
    const extensionPattern = new RegExp(`\\.(?:${["xlsx", "json", "csv"].join("|")})$`, "i");
    const baseName = trimmed.replace(extensionPattern, "").replace(/\s+/g, "_");
    return `${baseName}.${extension}`;
  };

  const stripExtension = (fileName: string): string => {
    if (!fileName) return "";
    const pattern = new RegExp(`\\.(?:${["xlsx", "json", "csv"].join("|")})$`, "i");
    return fileName.replace(pattern, "").trim();
  };

  const formatDateInput = (date?: Date | string): string | undefined => {
    if (!date) return undefined;
    const normalized = typeof date === "string" ? new Date(date) : date;
    if (Number.isNaN(normalized.getTime())) return undefined;
    const year = normalized.getFullYear();
    const month = String(normalized.getMonth() + 1).padStart(2, "0");
    const day = String(normalized.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const normalizeDateOnly = (date?: Date) => {
    if (!date || Number.isNaN(date.getTime())) return undefined;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const formatPreviewDate = (date: Date) =>
    date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getMonthlyScheduleValidation = (data: typeof scheduleData) => {
    const errors: Record<string, string> = {};
    const previewDates: Date[] = [];

    if (data?.frequency !== "Monthly") {
      return {
        errors,
        previewDates,
        nextExecutionDate: undefined as Date | undefined,
      };
    }

    const day = Number(data?.dayOfMonth?.value);
    const startDate = normalizeDateOnly(data?.startDate);
    const endDate = normalizeDateOnly(data?.endDate);

    if (!Number.isInteger(day) || day < 1 || day > 30) {
      errors.dayOfMonth = "Day must be between 1 and 30";
    }

    if (startDate && endDate && endDate < startDate) {
      errors.endDate = "End Date must be greater than Start Date";
    }

    const hasProvidedRange = Boolean(startDate && endDate && !errors.dayOfMonth && !errors.endDate);

    if (hasProvidedRange && startDate && endDate) {
      const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const stopMonth = endDate.getFullYear() * 12 + endDate.getMonth();

      while (cursor.getFullYear() * 12 + cursor.getMonth() <= stopMonth) {
        const candidate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
        const matchesMonth = candidate.getMonth() === cursor.getMonth();

        if (matchesMonth) {
          const candidateDate = normalizeDateOnly(candidate);
          if (candidateDate && candidateDate >= startDate && candidateDate <= endDate) {
            previewDates.push(candidateDate);
            if (previewDates.length >= 5) break;
          }
        }

        cursor.setMonth(cursor.getMonth() + 1);
      }
    }

    if (hasProvidedRange && !previewDates.length) {
      errors.monthlySchedule = "No valid schedule found for selected day in the given date range. Please adjust your Start Date, End Date, or Day of Month.";
    }

    return {
      errors,
      previewDates,
      nextExecutionDate: previewDates[0],
    };
  };

  const clearFieldErrors = (keys: string[]) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      keys.forEach((key) => {
        if (key in next) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  };

  const monthlyScheduleValidation = getMonthlyScheduleValidation(scheduleData);
  const isMonthlySubmitDisabled =
    scheduleData.frequency === "Monthly" && Object.keys(monthlyScheduleValidation.errors).length > 0;

  const normalizeEmailHtml = (html: string) => {
    if (!html) return html;
    if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const applyAlignment = (element: HTMLElement) => {
      const alignClass = Array.from(element.classList).find((className) => className.startsWith("ql-align-"));
      if (!alignClass) return;

      const alignment = alignClass.replace("ql-align-", "");
      if (!["left", "center", "right", "justify"].includes(alignment)) return;

      if (element.tagName === "LI") {
        const wrapper = doc.createElement("div");
        wrapper.style.textAlign = alignment;

        const childNodes = Array.from(element.childNodes);
        childNodes.forEach((child) => {
          const childElement = child instanceof HTMLElement ? child : null;
          if (childElement?.classList.contains("ql-ui")) return;
          wrapper.appendChild(child);
        });

        element.appendChild(wrapper);
        element.classList.remove(alignClass);
        return;
      }

      element.style.textAlign = alignment;
      element.classList.remove(alignClass);
    };

    const convertBulletLists = () => {
      const listContainers = Array.from(doc.querySelectorAll("ol"));

      listContainers.forEach((ol) => {
        const directItems = Array.from(ol.children).filter((child): child is HTMLLIElement => child.tagName === "LI");
        if (!directItems.length) return;

        const groupedBlocks: Array<{ tag: "ul" | "ol"; items: HTMLLIElement[] }> = [];

        directItems.forEach((li) => {
          const listType = li.getAttribute("data-list");
          const nextTag: "ul" | "ol" = listType === "bullet" ? "ul" : "ol";
          const lastBlock = groupedBlocks[groupedBlocks.length - 1];

          if (!lastBlock || lastBlock.tag !== nextTag) {
            groupedBlocks.push({ tag: nextTag, items: [li] });
          } else {
            lastBlock.items.push(li);
          }
        });

        const fragment = doc.createDocumentFragment();

        groupedBlocks.forEach((block) => {
          const listEl = doc.createElement(block.tag);
          listEl.style.listStyleType = block.tag === "ul" ? "disc" : "decimal";
          listEl.style.paddingLeft = "24px";
          listEl.style.margin = "0 0 0 24px";

          block.items.forEach((li) => {
            if (block.tag === "ul") {
              li.removeAttribute("data-list");
              li.style.listStyleType = "disc";
            } else {
              li.style.listStyleType = "decimal";
            }

            li.style.listStylePosition = "outside";
            listEl.appendChild(li);
          });

          fragment.appendChild(listEl);
        });

        ol.replaceWith(fragment);
      });
    };

    convertBulletLists();

    doc.querySelectorAll("[class*='ql-align-']").forEach((node) => {
      const element = node as HTMLElement;
      applyAlignment(element);
    });

    doc.querySelectorAll("ol, ul").forEach((node) => {
      const element = node as HTMLElement;
      if (!element.style.paddingLeft) {
        element.style.paddingLeft = "24px";
      }
      if (!element.style.margin) {
        element.style.margin = "0 0 0 24px";
      }
      element.style.listStylePosition = "outside";
    });

    return doc.body.innerHTML;
  };

  const validateScheduleData = (
    data: typeof scheduleData
  ): Record<string, string> => {
    const errors: Record<string, string> = {};
    const today = formatDateInput(new Date());
    const startDate = formatDateInput(data?.startDate);
    const endDate = formatDateInput(data?.endDate);

    // Require weekdays when Weekly is selected regardless of enabled state
    if (data?.frequency === "Weekly" && (!data?.daysOfWeek || data.daysOfWeek.length === 0)) {
      errors.daysOfWeek = "Please select at least one weekday.";
    }

    if (startDate && today && startDate < today) {
      errors.startDate = "Start Date cannot be in the past.";
    }

    if (endDate && today && endDate < today) {
      errors.endDate = "End Date cannot be in the past.";
    }

    if (startDate && endDate && endDate < startDate) {
      errors.endDate = "End Date cannot be before Start Date.";
    }

    if (data?.frequency === "Monthly") {
      const monthlyValidation = getMonthlyScheduleValidation(data);
      if (monthlyValidation.errors.dayOfMonth) errors.dayOfMonth = monthlyValidation.errors.dayOfMonth;
      if (monthlyValidation.errors.endDate) errors.endDate = monthlyValidation.errors.endDate;
      if (monthlyValidation.errors.monthlySchedule) errors.monthlySchedule = monthlyValidation.errors.monthlySchedule;
    }

    // If scheduling is not enabled, only the above Weekly check applies
    if (!data?.isEnabled) {
      return errors;
    }

    if (!data?.frequency) errors.frequency = "Required.";
    if (data?.frequency === "Hourly") {
      if (data?.intervalHours == null || data?.intervalHours < 1 || data?.intervalHours > 12) {
        errors.intervalHours = "Interval must be between 1 and 12.";
      }
    }
    if (data?.frequency !== "Hourly" && !data?.timeOfDay) errors.timeOfDay = "Required.";
    if (data?.frequency === "Weekly" && data?.daysOfWeek?.length === 0) {
      // already set above, keep for clarity
      errors.daysOfWeek = "Please select at least one weekday.";
    }
    return errors;
  };

  const getQueryById = async () => {
    try {
      if (!id) { showToast("Error", "ID is missing", "error"); return false; }
      const res = await getQueryData({ id: id }).unwrap();
      if (res?.error) { showToast("Action Failed", res?.error?.data?.message || `Failed to get query`, "error"); return false; }
      if (res) {
        // Map schedule data from scheduleSettings
        setScheduleData({
          isEnabled: res?.scheduledQuery?.scheduleSettings?.isEnabled,
          frequency:
            res?.scheduledQuery?.scheduleSettings?.frequency?.charAt(0).toUpperCase() +
            res?.scheduledQuery?.scheduleSettings?.frequency?.slice(1).toLowerCase(),
          daysOfWeek:
            res?.scheduledQuery?.scheduleSettings?.daysOfWeek &&
            res?.scheduledQuery?.scheduleSettings?.daysOfWeek.map((d: any) => dayIndexToStringMap[d]),
          startDate: res?.scheduledQuery?.scheduleSettings?.startDate && new Date(res?.scheduledQuery?.scheduleSettings?.startDate),
          endDate: res?.scheduledQuery?.scheduleSettings?.endDate && new Date(res?.scheduledQuery?.scheduleSettings?.endDate),
          dayOfMonth: {
            value: res?.scheduledQuery?.scheduleSettings?.dayOfMonth && String(res?.scheduledQuery?.scheduleSettings?.dayOfMonth),
            label: res?.scheduledQuery?.scheduleSettings?.dayOfMonth && String(res?.scheduledQuery?.scheduleSettings?.dayOfMonth),
          },
          timeOfDay: res?.scheduledQuery?.scheduleSettings?.timeOfDay || "",
          intervalHours: res?.scheduledQuery?.scheduleSettings?.intervalHours || 1,
        });

        // Map email config
        setFromEmail(res?.scheduledQuery?.emailConfig?.from || "");
        setEmailTags({
          to: res?.scheduledQuery?.emailConfig?.to || [],
          cc: res?.scheduledQuery?.emailConfig?.cc || [],
          bcc: res?.scheduledQuery?.emailConfig?.bcc || [],
        });
        // Map carbon-copy flag from backend (do not infer from cc/bcc arrays)
        setIsCarbonCopy(Boolean(res?.scheduledQuery?.emailConfig?.isCarbonCopy));
        setSubject(res?.scheduledQuery?.emailConfig?.subject || "");
        setMessageBody(normalizeEmailHtml(res?.scheduledQuery?.emailConfig?.message || ""));

        // Map output settings - store base filename (no extension) in the input
        const nextOutputFormat = res?.scheduledQuery?.outputFormat || "CSV";
        setOutputFormat(nextOutputFormat);
        setOutputFileName(stripExtension(res?.scheduledQuery?.fileNameTemplate || res?.name || "").replace(/\s+/g, "_"));
        setSkipIfEmpty(res?.scheduledQuery?.skipIfEmpty || false);

        return res?.id;
      }
    } catch (err: any) {
      const message = err?.data?.message || 'Failed to get query'
      showToast("Error", message, "error");
      return false;
    }
  };

  useEffect(() => {
    if (id) getQueryById();
  }, [id]);

  const handleSubmit = async () => {
    const errors = validateScheduleData(scheduleData);

    const nextEmailTags = {
      ...emailTags,
    };

    const toDraftParts = parseEmailParts(emailDrafts.to);
    if (toDraftParts.length > 0) {
      const invalidToPart = toDraftParts.find((part) => !emailPattern.test(part));
      if (invalidToPart) {
        errors.to = "Enter a valid email address";
      } else {
        nextEmailTags.to = Array.from(new Set([...nextEmailTags.to, ...toDraftParts]));
      }
    }

    // Add required field validation
    if (!fromEmail.trim()) {
      errors.from = "From is required.";
    }
    if (nextEmailTags.to.length === 0 && !errors.to) {
      errors.to = "To is required";
    }
    if (!subject.trim()) {
      errors.subject = "Subject is required.";
    }
    if (!outputFileName.trim()) {
      errors.outputFileName = "Output File Name is required.";
    }

    if (scheduleData.frequency === "Monthly") {
      Object.assign(errors, monthlyScheduleValidation.errors);
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const mappedData = {
      scheduleSettings: {
        frequency: scheduleData.frequency.toLocaleUpperCase(),
        intervalHours: scheduleData.frequency === "Hourly" ? scheduleData.intervalHours : undefined,
        timeOfDay: scheduleData.frequency !== "Hourly" ? scheduleData.timeOfDay : undefined,
        daysOfWeek:
          scheduleData.frequency === "Weekly" && Array.isArray(scheduleData.daysOfWeek)
            ? scheduleData.daysOfWeek.map((d) => dayStringToIndexMap[d])
            : undefined,
        dayOfMonth:
          scheduleData.frequency === "Monthly"
            ? Number(scheduleData.dayOfMonth?.value)
            : undefined,
        isEnabled: scheduleData.isEnabled,
        startDate: scheduleData.startDate?.toISOString(),
        endDate: scheduleData.endDate?.toISOString(),
        timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions)
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'UTC',
      },
      fileNameTemplate: formatOutputFileName(outputFileName, outputFormat),
      outputFormat: outputFormat,
      skipIfEmpty: skipIfEmpty,
      emailConfig: {
        from: fromEmail,
        to: nextEmailTags.to,
        cc: emailTags.cc,
        bcc: emailTags.bcc,
        isCarbonCopy: isCarbonCopy,
        subject: subject,
        message: normalizeEmailHtml(messageBody),
      },
    };

    try {
      // console.log(mappedData,'mappedData');

      const res = await scheduleQuery({ id: id, ...mappedData }).unwrap();
      if (res?.error) { showToast("Action Failed", res?.error?.message || res?.error?.error || `Failed to schdule query`, "error"); return false; }
      if (res) {
        showToast("Success", `Query scheduled successfully`, "success");
        refetch && refetch();
        onClose();
        return res?.id;
      }
    } catch (err: any) {
      const message =
        err?.data?.message ||
        err?.error || 'Failed to schedule query'
      showToast("Error", message, "error");
      return false;
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered>
      <StyledModalContent theme={theme}>
        {/* Header */}
        <StyledHeader bgImage={bgImage}>
          <div>
            <StyledText fontSize="20px" fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>{title ?? "Schedule Export"}</StyledText>
            <StyledText fontSize="15px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} lineHeight="1.5" className="mt-2">
              {subTitle ?? (
                <>
                  Use the options below to modify your query criteria and get results that
                  <br />
                  better align with your goals
                </>
              )}
            </StyledText>
          </div>
          <StyledCloseButton onClick={onClose} theme={theme}>×</StyledCloseButton>
        </StyledHeader>

        <ModalBody>
          <ModalBodyScroll>
            {getQueryLoading ? (
              <TwoColLayout>
                <LeftPanel theme={theme}>

                  {Array(5).fill(null).map((_, i) => (
                    <SkeletonCell key={i} style={{ height: "36px", marginBottom: "14px", borderRadius: "6px" }} />
                  ))}
                </LeftPanel>
                <RightPanel theme={theme}>
                  {Array(6).fill(null).map((_, i) => (
                    <SkeletonCell key={i} style={{ height: "36px", marginBottom: "14px", borderRadius: "6px" }} />
                  ))}
                </RightPanel>
              </TwoColLayout>
            ) : (
              <TwoColLayout>
                {/* ── LEFT PANEL ── */}
                <LeftPanel theme={theme}>
                  <FieldGroup>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <StyledSwitch
                          checked={scheduleData?.isEnabled}
                          onChange={() => setScheduleData((prev) => ({ ...prev, isEnabled: !prev?.isEnabled }))}
                          thumbSize={10}
                          height={13}
                          width={23}
                          switchColor={theme.CorporateTheme?.colors?.blueberry}
                        />
                        <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>
                          Enable/Disable Scheduling against this export?
                        </StyledText>
                      </div>
                    </div>
                  </FieldGroup>
                  {/* Scheduling Section - disabled when scheduling is off */}
                  <SchedulingSection $isEnabled={scheduleData?.isEnabled}>
                    {/* Scheduling Options */}
                    <FieldGroup>
                      <StyledText fontSize="15px" fontWeight={600} textColor={theme.CorporateTheme.colors.heavyMetal} className="mb-2">Scheduling Options</StyledText>
                      <ScheduleOptionsRow>
                        {["Hourly", "Daily", "Weekly", "Monthly"].map((inter, index) => (
                          <ScheduleOptionButton
                            theme={theme}
                            $isSelected={inter === scheduleData?.frequency}
                            onClick={() => {
                              setScheduleData((prev) => ({ ...prev, frequency: inter }));
                              setFieldErrors((prev) => {
                                const copy = { ...prev };
                                if (copy.frequency) delete copy.frequency;
                                return copy;
                              });
                            }}
                            key={index}
                          >
                            <StyledText fontSize="13px" fontWeight={600} textColor={inter === scheduleData?.frequency ? theme.CorporateTheme.colors.headerColor : theme.CorporateTheme.colors.textSecondary}>
                              {inter}
                            </StyledText>
                          </ScheduleOptionButton>
                        ))}
                      </ScheduleOptionsRow>
                      {fieldErrors.frequency && <ErrorText>{fieldErrors.frequency}</ErrorText>}
                    </FieldGroup>

                    {/* Run On */}
                    {(scheduleData?.frequency === "Weekly" || scheduleData?.frequency === "Monthly") && (
                      <FieldGroup>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.heavyMetal} className="mb-2 mt-2">This Query Will Run On</StyledText>
                        </div>
                        {scheduleData?.frequency === "Weekly" ? (
                          <>
                            <DaysRow theme={theme}>
                              {[
                                { key: "Sun", label: "Sunday" },
                                { key: "Mon", label: "Monday" },
                                { key: "Tue", label: "Tuesday" },
                                { key: "Wed", label: "Wednesday" },
                                { key: "Thurs", label: "Thursday" },
                                { key: "Fri", label: "Friday" },
                                { key: "Sat", label: "Saturday" },

                              ].map((d, index) => {
                                const isChecked = scheduleData.daysOfWeek?.includes(d.key);
                                return (
                                  <DayChip
                                    key={index}
                                    theme={theme}
                                    isChecked={isChecked}
                                    onClick={() => handleDayToggle(d.key)}
                                  >
                                    <StyledCheckBox
                                      className="cursor-pointer d-flex align-items-center"
                                      size={14}
                                      borderColor={isChecked ? theme.CorporateTheme?.colors?.primary : "#626262"}
                                      bgColor={theme.CorporateTheme?.colors?.primary}
                                      checkColor={theme.colors.contentBackground}
                                      label={d.label}
                                      value={isChecked}
                                      labelSize="12px"
                                      onChange={() => handleDayToggle(d.key)}
                                    />
                                  </DayChip>
                                );
                              })}
                            </DaysRow>
                            {fieldErrors.daysOfWeek && <ErrorText>{fieldErrors.daysOfWeek}</ErrorText>}
                          </>
                        ) : (
                          <>
                            <CustomSelect
                              options={Array.from({ length: 30 }, (_, i) => (i + 1).toString())}
                              value={
                                scheduleData?.dayOfMonth?.label
                                  ? { label: `Day ${scheduleData.dayOfMonth?.label} of each Month`, value: scheduleData.dayOfMonth?.value }
                                  : ""
                              }
                              onChange={(val: any) => {
                                setScheduleData({ ...scheduleData, dayOfMonth: val });
                                clearFieldErrors(["dayOfMonth", "endDate", "monthlySchedule"]);
                              }}
                              placeholder="Select day of months"
                              selectedValueColor={theme.CorporateTheme?.colors?.headerColor}
                            />
                            {(fieldErrors.dayOfMonth || monthlyScheduleValidation.errors.dayOfMonth) && (
                              <ErrorText>{fieldErrors.dayOfMonth || monthlyScheduleValidation.errors.dayOfMonth}</ErrorText>
                            )}
                            {monthlyScheduleValidation.errors.monthlySchedule && (
                              <ErrorText>{monthlyScheduleValidation.errors.monthlySchedule}</ErrorText>
                            )}
                          </>
                        )}
                      </FieldGroup>
                    )}

                    {/* Time */}
                    <FieldGroup>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.heavyMetal} className="mb-2 mt-2">Time</StyledText>
                      <TimeRow>
                        {scheduleData?.frequency === "Hourly" ? (
                          <div className="d-flex align-items-center gap-2">
                            <TimeInputWrap>
                              <StyledStepper
                                label="Hour"
                                min={1}
                                max={12}
                                defaultVal={scheduleData?.intervalHours}
                                onChange={(value) => { setScheduleData({ ...scheduleData, intervalHours: value }); clearFieldErrors(["intervalHours"]); }}
                              />
                              {fieldErrors.intervalHours && <ErrorText>{fieldErrors.intervalHours}</ErrorText>}
                            </TimeInputWrap>
                            <StyledText textColor={theme?.CorporateTheme?.colors?.accentOrange} fontSize="12px" fontWeight={500}><TooltipIcon color={theme?.CorporateTheme?.colors?.accentOrange} /> All times are sent in EST</StyledText>
                          </div>
                        ) : (
                          <>
                            <TimeInputWrap>
                              <StyledTimePicker
                                value={scheduleData?.timeOfDay}
                                onChange={(value) => { setScheduleData({ ...scheduleData, timeOfDay: value }); clearFieldErrors(["timeOfDay"]); }}
                              />
                              {scheduleData.frequency !== "Hourly" && fieldErrors.timeOfDay && (
                                <ErrorText>{fieldErrors.timeOfDay}</ErrorText>
                              )}
                            </TimeInputWrap>
                            <StyledText textColor={theme?.CorporateTheme?.colors?.accentOrange} fontStyle="italic" fontSize="12px" fontWeight={500}><TooltipIcon color={theme?.CorporateTheme?.colors?.accentOrange} /> All times are sent in EST</StyledText>
                          </>
                        )}
                      </TimeRow>
                    </FieldGroup>

                    {/* Start Date */}
                    <FieldGroup>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.heavyMetal} className="mb-2 mt-2">Start Date</StyledText>
                      <CustomInput
                        type="date"
                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                        value={scheduleData?.startDate ? scheduleData.startDate.toISOString().split("T")[0] : ""}
                        onChange={(value: string) => {
                          setScheduleData({ ...scheduleData, startDate: value ? new Date(value) : undefined });
                          clearFieldErrors(["startDate", "endDate", "monthlySchedule"]);
                        }}
                        min={formatDateInput(new Date())}
                        max={scheduleData?.endDate ? formatDateInput(scheduleData.endDate) : undefined}
                        error={fieldErrors.startDate}
                      />
                    </FieldGroup>

                    {/* End Date */}
                    <FieldGroup>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.heavyMetal} className="mb-2 mt-2">End Date</StyledText>
                      <CustomInput
                        type="date"
                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                        placeholder="Select end Date"
                        value={scheduleData?.endDate ? scheduleData.endDate.toISOString().split("T")[0] : ""}
                        onChange={(value: string) => {
                          setScheduleData({ ...scheduleData, endDate: value ? new Date(value) : (undefined as any) });
                          clearFieldErrors(["endDate", "monthlySchedule"]);
                        }}
                        min={scheduleData?.startDate ? formatDateInput(scheduleData.startDate) : formatDateInput(new Date())}
                        error={fieldErrors.endDate || monthlyScheduleValidation.errors.endDate}
                      />
                    </FieldGroup>
                    {/* {scheduleData.frequency === "Monthly" && (
                    <MonthlyPreviewBox theme={theme}>
                      <StyledText fontSize="12px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                        Monthly Schedule Preview
                      </StyledText>
                      {monthlyScheduleValidation.nextExecutionDate ? (
                        <>
                          <MonthlyPreviewRow>
                            <StyledText fontSize="12px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
                              Next execution date
                            </StyledText>
                            <StyledText fontSize="13px" fontWeight={600} textColor={theme.CorporateTheme.colors.blueberry}>
                              {formatPreviewDate(monthlyScheduleValidation.nextExecutionDate)}
                            </StyledText>
                          </MonthlyPreviewRow>
                          <StyledText fontSize="12px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
                            Upcoming occurrences
                          </StyledText>
                          <MonthlyOccurrenceList>
                            {monthlyScheduleValidation.previewDates.map((date) => (
                              <span key={date.toISOString()}>{formatPreviewDate(date)}</span>
                            ))}
                          </MonthlyOccurrenceList>
                        </>
                      ) : (
                        <ErrorText>{monthlyScheduleValidation.errors.monthlySchedule || "Select a valid day and date range to preview occurrences."}</ErrorText>
                      )}
                    </MonthlyPreviewBox>
                  )} */}
                  </SchedulingSection>
                </LeftPanel>

                {/* ── RIGHT PANEL ── */}
                <RightPanel theme={theme}>
                  <StyledText fontSize="15px" fontWeight={600} textColor={theme.CorporateTheme.colors.primary} fontStyle="italic">Delivery Email Settings</StyledText>

                  {/* From */}
                  <FieldGroup>
                    <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mt-3 mb-2">From</StyledText>
                    <CustomSelect
                      options={mappedFromEmailOptions}
                      value={fromEmail ? { label: fromEmail, value: fromEmail } : ""}
                      onChange={(val: any) => { setFromEmail(val?.value ?? val); setFieldErrors((prev) => ({ ...prev, from: "" })); }}
                      placeholder="Select from email"
                      selectedValueColor={theme.CorporateTheme?.colors?.headerColor}
                      error={fieldErrors.from}
                    />
                  </FieldGroup>

                  {/* To */}
                  <EmailSection>
                    <ToLabelRow>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>To</StyledText>
                      {/* <EnableCcText> */}
                      <div>
                        <StyledCheckBox
                          className="cursor-pointer d-flex align-items-center me-sm-0"
                          size={14}
                          marginRight="0"
                          borderColor={isCarbonCopy ? theme.CorporateTheme?.colors?.primary : "#626262"}
                          bgColor={theme.CorporateTheme?.colors?.primary}
                          checkColor={theme.colors.contentBackground}
                          label="Enable Cc &amp; Bcc"
                          value={isCarbonCopy}
                          labelSize="12px"
                          onChange={() => setIsCarbonCopy((prev) => !prev)}
                        />
                      </div>
                      {/* </EnableCcText> */}
                    </ToLabelRow>
                    <CustomInput
                      value={emailDrafts.to}
                      onChange={(value: string) => {
                        setEmailDrafts((prev) => ({ ...prev, to: value }));
                        setEmailErrors((prev) => ({ ...prev, to: "" }));
                        if (value.trim()) {
                          setFieldErrors((prev) => {
                            if (!prev.to) return prev;
                            const next = { ...prev };
                            delete next.to;
                            return next;
                          });
                        }
                      }}
                      onKeyDown={(event) => handleEmailKeyDown("to", event)}
                      onBlur={() => handleEmailBlur("to")}
                      placeholder="Recipients"
                      className="px-3"
                      error={fieldErrors.to || emailErrors.to}
                    />
                    {emailTags.to.length > 0 ? <TagRow className="mb-3">
                      {emailTags.to.map((tag) => (
                        <OutlinedTag key={tag} theme={theme}>
                          <StyledText fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.blueberry}>{tag}</StyledText>
                          {/* <button type="button" onClick={() => removeEmailTag("to", tag)}>
                            ×
                          </button> */}
                          <CloseIcon color={theme.CorporateTheme.colors.graniteGrey} size="12px" onClick={() => removeEmailTag("to", tag)} />
                        </OutlinedTag>
                      ))}
                    </TagRow> : null}

                    {isCarbonCopy && (
                      <CcBccPanel theme={theme} className="mb-3">
                        <EmailSubSection>
                          <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-2">Cc</StyledText>
                          <CustomInput
                            value={emailDrafts.cc}
                            onChange={(value: string) => {
                              setEmailDrafts((prev) => ({ ...prev, cc: value }));
                              setEmailErrors((prev) => ({ ...prev, cc: "" }));
                            }}
                            onKeyDown={(event) => handleEmailKeyDown("cc", event)}
                            onBlur={() => handleEmailBlur("cc")}
                            placeholder="Cc"
                            className="px-3"
                            error={emailErrors.cc}
                          />
                          <TagRow className="mb-3">
                            {emailTags.cc.map((tag) => (
                              <OutlinedTag key={tag} theme={theme}>
                                <StyledText fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.blueberry}>
                                  {tag}
                                </StyledText>
                                <CloseIcon color={theme.CorporateTheme.colors.graniteGrey} size="12px" onClick={() => removeEmailTag("cc", tag)} />
                              </OutlinedTag>
                            ))}
                          </TagRow>
                        </EmailSubSection>

                        <EmailSubSection>
                          <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-2">Bcc</StyledText>
                          <CustomInput
                            value={emailDrafts.bcc}
                            onChange={(value: string) => {
                              setEmailDrafts((prev) => ({ ...prev, bcc: value }));
                              setEmailErrors((prev) => ({ ...prev, bcc: "" }));
                            }}
                            onKeyDown={(event) => handleEmailKeyDown("bcc", event)}
                            onBlur={() => handleEmailBlur("bcc")}
                            placeholder="Bcc"
                            className="px-3"
                            error={emailErrors.bcc}
                          />
                          <TagRow className="mb-3">
                            {emailTags.bcc.map((tag) => (
                              <OutlinedTag key={tag} theme={theme}>
                                <StyledText fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.blueberry}>{tag}</StyledText>
                                <CloseIcon color={theme.CorporateTheme.colors.graniteGrey} size="12px" onClick={() => removeEmailTag("bcc", tag)} />
                              </OutlinedTag>
                            ))}
                          </TagRow>
                        </EmailSubSection>
                      </CcBccPanel>
                    )}
                  </EmailSection>

                  {/* Subject */}
                  <FieldGroup>
                    <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-2 mt-2">Subject</StyledText>
                    <CustomInput
                      placeholder="Subject"
                      value={subject}
                      onChange={(value: string) => { setSubject(value); setFieldErrors((prev) => ({ ...prev, subject: "" })); }}
                      className="px-3"
                      error={fieldErrors.subject}
                    />
                  </FieldGroup>

                  {/* Rich text toolbar placeholder */}
                  <FieldGroup>
                    <StyledTextEditor
                      value={messageBody}
                      onChange={setMessageBody}
                      placeholder="Write your message"
                      minHeight="160px"
                      isReadOnly={false}
                      toolbarBg={theme.CorporateTheme?.colors?.filterBG}
                      bodyBg={theme.CorporateTheme?.colors?.background}
                      borderColor={theme.CreateFormTheme?.colors?.inputBorders}
                      fontFamily={font.raleway}
                    />
                  </FieldGroup>

                  {/* Output format + file name */}
                  <OutputRow>
                    <FieldGroup style={{ flex: 1 }}>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-2">Search Query Output Format</StyledText>
                      <CustomSelect
                        options={[{ label: "Excel", value: "EXCEL" }, { label: "Json", value: "JSON" }, { label: "Csv", value: "CSV" }]}
                        value={outputFormat ? { label: getOutputFormatLabel(outputFormat), value: outputFormat } : ""}
                        onChange={(val: any) => {
                          const nextFormat = val?.value ?? val;
                          setOutputFormat(nextFormat);
                          // keep the input as base name (no extension); normalize spaces
                          setOutputFileName((current) => (current || "").replace(/\s+/g, "_"));
                        }}
                        placeholder="Format"
                        selectedValueColor={theme.CorporateTheme?.colors?.headerColor}
                      />
                    </FieldGroup>
                    <FieldGroup style={{ flex: 1 }}>
                      <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-2">Output File Name</StyledText>
                      <CustomInput
                        value={outputFileName}
                        onChange={(value: string) => {
                          // live replace spaces with underscores in the editable base name
                          const cleaned = value.replace(/\s+/g, "_");
                          setOutputFileName(cleaned);
                          setFieldErrors((prev) => ({ ...prev, outputFileName: "" }));
                        }}
                        onBlur={() => {
                          // If user pasted a full filename, strip any extension; normalize spaces
                          setOutputFileName(stripExtension(outputFileName).replace(/\s+/g, "_"));
                        }}
                        placeholder="Enter file name"
                        type="text"
                        className="px-3"
                        error={fieldErrors.outputFileName}
                      />
                    </FieldGroup>
                  </OutputRow>

                  {/* Filename preview */}
                  <FilenamePreviewBox theme={theme}>
                    <StyledText fontSize="12px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Filename Preview</StyledText>
                    <FilenamePreviewRow>
                      <PreviewIconWrap theme={theme}>
                        <VerifyIcon size={18} color={theme.CorporateTheme?.colors?.blueberry} />
                      </PreviewIconWrap>
                      <StyledText
                        fontSize="12px"
                        fontWeight={500}
                        textColor={theme?.CorporateTheme?.colors?.headerColor}
                      >
                        Will be sent as:
                      </StyledText>
                      <FilenameLink theme={theme}><StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.blueberry}>{formatOutputFileName(outputFileName, outputFormat)}</StyledText></FilenameLink>
                    </FilenamePreviewRow>
                  </FilenamePreviewBox>
                </RightPanel>
              </TwoColLayout>
            )}
          </ModalBodyScroll>

          {/* Footer */}
          <StyledFooter theme={theme}>
            {getQueryLoading ? (
              <>
                <FooterLeft>
                  <SkeletonCell style={{ width: "26px", height: "16px", borderRadius: "10px" }} />
                  <SkeletonCell style={{ width: "280px", height: "16px", borderRadius: "6px" }} />
                </FooterLeft>
                <FooterRight>
                  <SkeletonCell style={{ width: "92px", height: "40px", borderRadius: "8px" }} />
                  <SkeletonCell style={{ width: "164px", height: "40px", borderRadius: "8px" }} />
                </FooterRight>
              </>
            ) : (
              <>
                <FooterLeft>
                  <StyledSwitch
                    checked={skipIfEmpty}
                    onChange={() => setSkipIfEmpty((prev) => !prev)}
                    thumbSize={10}
                    height={13}
                    width={23}
                    switchColor={theme.CorporateTheme?.colors?.blueberry}
                  />
                  <StyledText
                    fontSize="14px"
                    fontWeight={500}
                    textColor={theme.CorporateTheme?.colors?.headerColor}
                  >
                    Don't send email if search result is empty
                  </StyledText>
                </FooterLeft>
                <FooterRight>
                  <Button
                    theme={theme}
                    type="button"
                    className={`cursor-pointer ${isLoading ? "class-disable" : ""}`}
                    $variant="customButton"
                    btnBg={theme.CorporateTheme.colors.background}
                    color={theme.CorporateTheme.colors.headerColor}
                    onClick={onClose}
                    borderColor="none"
                  >
                    Close
                  </Button>
                  <Button
                    theme={theme}
                    type="button"
                    className={`cursor-pointer ${isLoading || isMonthlySubmitDisabled ? "class-disable" : ""}`}
                    $variant="customButton"
                    onClick={handleSubmit}
                    btnBg={theme.CorporateTheme.colors.primary}
                    color={theme.CorporateTheme.colors.background}
                    borderColor="none"
                    disabled={isLoading || isMonthlySubmitDisabled}
                  >
                    {isLoading ? (
                      <>
                        <label>Saving...</label>
                        <LoadingCircleIcon />
                      </>
                    ) : (
                      "Schedule This Export"
                    )}
                  </Button>
                  {/* <ScheduleButton
                theme={theme}
                type="submit"
                className={`cursor-pointer ${isLoading ? "class-disable" : ""}`}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <>
                    <label>Saving...</label>
                    <LoadingCircleIcon />
                  </>
                ) : (
                  "Schedule This Export"
                )}
              </ScheduleButton> */}
                </FooterRight>
              </>
            )}
          </StyledFooter>
        </ModalBody>
      </StyledModalContent>
    </Modal>
  );
};

export default ScheduleQueryModal;

/* ─── Styled Components ─────────────────────────────────────── */

const StyledCloseButton = styled(ModalCloseButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 20px;
  cursor: pointer;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
  background: none;
  border: none;
  line-height: 1;
`;

const StyledHeader = styled.div <{ bgImage: string }>`
  position: relative;
  text-align: center;
  padding: 24px 24px 0;
  background-image: ${({ bgImage }) => `url(${bgImage})`};
  background-size: contain;
  background-position: center;
  border-radius: 8px 8px 0 0;
  background-repeat: no-repeat;
`;

const StyledModalTitle = styled.h5`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 6px;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.headerColor};
`;

const StyledSubTitle = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
  margin-bottom: 0;
`;

const ModalBodyScroll = styled.div`
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  padding-right: 4px;
`;

const TwoColLayout = styled.div`
  display: flex;
  gap: 0;
  padding: 16px 20px;
  min-height: 480px;
`;

const LeftPanel = styled.div`
  flex: 0 0 442px;
  width: 300px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border};
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
  display: flex;
  flex-direction: column;
  border-radius: 5px 0 0 5px;
  gap: 4px;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme?.CreateFormTheme?.colors?.addressBG};
  gap: 4px;
`;

const RightPanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.primary ?? "#0066cc"};
  margin-bottom: 12px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.headerColor ?? "#1a1a1a"};
  margin-bottom: 6px;
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const TimeInputWrap = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
`;

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EstNote = styled.span`
  font-size: 11px;
  color: #e05252;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScheduleOptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ScheduleOptionButton = styled.button<{ theme: any; $isSelected?: boolean }>`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ theme, $isSelected }) =>
    $isSelected ? theme?.CorporateTheme?.colors?.accentOrange : theme?.CorporateTheme?.colors?.border};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? hexToRgba(theme?.CorporateTheme?.colors?.accentOrange, 0.1) : theme?.CorporateTheme?.colors?.background};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme?.CorporateTheme?.colors?.accentOrange : theme?.CorporateTheme?.colors?.textPrimary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  margin: 0;
  border-radius: 0;
  white-space: nowrap;
  position: relative;
  z-index: ${({ $isSelected }) => ($isSelected ? 2 : 1)};

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:not(:first-child) {
    margin-left: -1px;
  }

  &:hover {
    background-color: ${({ theme }) => hexToRgba(theme?.CorporateTheme?.colors?.accentOrange, 0.1)};
    border-color: ${({ theme }) => theme?.CorporateTheme?.colors?.accentOrange};
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.accentOrange};
    z-index: 3;
  }

  ${({ $isSelected, theme }) =>
    $isSelected && `
      font-weight: 600;
      background-color: ${hexToRgba(theme?.CorporateTheme?.colors?.accentOrange, 0.1)};
      border-color: ${theme?.CorporateTheme?.colors?.accentOrange};
      color: ${theme?.CorporateTheme?.colors?.accentOrange};
      z-index: 2;
    `}
`;

const DaysRow = styled.div<{ theme: Theme }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme?.CreateFormTheme?.colors?.newBgColor};
  border-radius: 8px;
`;

const DayChip = styled.div<{ isChecked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
`;

const ToLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EnableCcText = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: ${({ theme }) => theme?.CorporateTheme?.colors?.primary};
    cursor: pointer;
  }
`;

const EmailInput = styled.input`
  width: 100%;
  min-height: 44px;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#d6d6d6"};
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.headerColor};
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background ?? "#fff"};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
  }

  &:focus {
    border-color: ${({ theme }) => theme?.CorporateTheme?.colors?.primary};
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const OutlinedTag = styled(StyledTag) <{ theme: Theme }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  height: auto;
  font-size: 12px;
  font-weight: 500;
  border-radius: 3px;
  background:  ${({ theme }) => theme?.CorporateTheme?.colors?.background};
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.blueberry};
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.blueberry};

  span {
    line-height: 1;
  }

  button {
    border: none;
    background: transparent;
    color: inherit;
    font-size: 14px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
  }
`;

const EmailSubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CcBccPanel = styled.div<{ theme: Theme }>`
  display: flex;
  gap: 6px;
  flex-direction: column;
  padding: 16px 14px 14px;
  border-radius: 4px;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background ?? "#fff"};
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#e8e8e8"};
  margin-top: 8px;
`;

const EmailError = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: -5px;
`;

const SubjectInput = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#e0e0e0"};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textPrimary};
  background: ${({ theme }) => theme?.colors?.contentBackground ?? "#fff"};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme?.CorporateTheme?.colors?.primary};
  }
`;

const RichToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#e0e0e0"};
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: ${({ theme }) => theme?.colors?.contentBackground ?? "#fff"};
  flex-wrap: wrap;
`;

const ToolbarBtn = styled.button`
  background: none;
  border: none;
  padding: 2px 6px;
  font-size: 13px;
  cursor: pointer;
  border-radius: 3px;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textPrimary};

  &:hover {
    background: ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#f0f0f0"};
  }
`;

const StylesBtn = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#e0e0e0"};
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
  border-radius: 3px;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textPrimary};
`;

const RichBody = styled.div`
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border ?? "#e0e0e0"};
  border-radius: 0 0 6px 6px;
  padding: 12px;
  min-height: 110px;
  background: ${({ theme }) => theme?.colors?.contentBackground ?? "#fff"};
`;

const RichBodyText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme?.CorporateTheme?.colors?.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const OutputRow = styled.div`
  display: flex;
  gap: 16px;
`;

const FilenamePreviewBox = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.blueberry};
  border-radius: 10px;
  padding: 18px 20px 22px;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
`;

const MonthlyPreviewBox = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border};
  border-radius: 10px;
  padding: 16px 18px;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MonthlyPreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const MonthlyOccurrenceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  > span {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }
`;

const FilenamePreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
`;

const PreviewIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const FilenameLink = styled.span<{ theme: Theme }>`
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border};
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
  font-size: 18px;
  font-weight: 400;
  text-decoration: none;
  cursor: default;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

const CustomSelectWrapper = styled.div<{ $hasError?: boolean }>`
  & > div {
  border-radius:5px;
    border: 1px solid ${({ $hasError }) => $hasError ? "#dc3545" : "inherit"} !important;
  }
`;

const SchedulingSection = styled.div<{ $isEnabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: ${({ $isEnabled }) => ($isEnabled ? 1 : 0.5)};
  pointer-events: ${({ $isEnabled }) => ($isEnabled ? "auto" : "none")};
  transition: opacity 0.3s ease;
`;

const StyledFooter = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.filterBG};
  z-index: 2;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ScheduleButton = styled.button`
  padding: 8px 20px;
  border-radius: 6px;
  border: none;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.primary ?? "#1a3c8f"};
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &.class-disable {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;